// @flow
import clamp from 'lodash/clamp';

export type CardDisplaysType = {
  displayCount: number,
  gutter: number,
  height: number,
  minWidth: number
}[];

export function getSprings(
  cardDisplays: CardDisplaysType,
  parentElement: ?HTMLDivElement,
  currentSelectedRef: *,
  setSelectedIndex: (number) => void,
  cardsLength: number,
  activeIndex?: number,
  gesture?: {
    cancel: Function,
    delta: number[],
    direction: number[],
    distance: number,
    down: boolean,
    event: MouseEvent
  }
): [
  boolean,
  number,
  (
    index: number
  ) => {
    height: number,
    opacity: number,
    translateX: number,
    width: number
  }
] {
  const windowWidth =
    typeof window !== 'undefined'
      ? Math.min(window.outerWidth, window.innerWidth)
      : 0;
  let displaySize = cardDisplays[0];
  for (
    let i = 0;
    i < cardDisplays.length && cardDisplays[i].minWidth <= windowWidth;
    i += 1
  ) {
    displaySize = cardDisplays[i];
  }
  const { gutter, height: cardHeight } = displaySize;
  const displayCount = Math.min(displaySize.displayCount, cardsLength);
  const parentWidth = parentElement
    ? parentElement.getBoundingClientRect().width
    : windowWidth;
  const cardWidth = (parentWidth - (displayCount - 1) * gutter) / displayCount;
  const cardMovement = cardWidth + gutter;
  let selectedIndex = currentSelectedRef.current;
  const originalSelectedIndex = currentSelectedRef.current;
  const clampedActiveIndex = clamp(
    activeIndex || 0,
    0,
    cardsLength - displayCount
  );

  if (
    typeof activeIndex !== 'undefined' &&
    clampedActiveIndex !== currentSelectedRef.current
  ) {
    currentSelectedRef.current = clampedActiveIndex;
    setSelectedIndex(clampedActiveIndex);
    selectedIndex = clampedActiveIndex;
  }

  let indicesTravelled = 0;
  if (gesture) {
    const {
      event,
      down,
      distance,
      delta: [xDelta]
    } = gesture;
    event.preventDefault();
    event.stopPropagation();
    indicesTravelled = distance / cardMovement;
    if (down) {
      selectedIndex += (xDelta > 0 ? -1 : 1) * indicesTravelled;
    } else {
      selectedIndex = clamp(
        Math.round(selectedIndex + (xDelta > 0 ? -1 : 1) * indicesTravelled),
        0,
        cardsLength - displayCount
      );
      setSelectedIndex(selectedIndex);
    }
  }

  const isAtRightEnd = selectedIndex >= cardsLength - displayCount;
  return [
    isAtRightEnd,
    cardHeight,
    (i: number) => {
      let opacity = 0;
      // first render should be opacity: 0, this is when selectedIndex is -1
      if (selectedIndex !== -1) {
        if (i > selectedIndex - 1 && i < selectedIndex + displayCount) {
          opacity = 1;
        } else if (
          i === selectedIndex - 1 ||
          i === selectedIndex + displayCount
        ) {
          opacity = 0.85;
        } else if (
          i === selectedIndex - 2 ||
          i === selectedIndex + displayCount + 1
        ) {
          opacity = 0.4;
        }
      }
      return {
        // this improves load performance _significantly_
        immediate: originalSelectedIndex === -1,
        translateX: (i - selectedIndex) * cardMovement,
        opacity,
        width: cardWidth,
        height: cardHeight
      };
    }
  ];
}
